import React, {useEffect, useMemo, useRef, useState} from "react"
import {useNavigate} from "react-router-dom"
// MUI
import {
    Box, Button, Checkbox, Divider, IconButton, ListItemIcon, ListItemText,
    Menu, MenuItem, Skeleton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {
    Delete, Download, Downloading, Edit, Inventory, Link as LinkIcon, QueryStats, Unarchive,
    ViewColumnOutlined, DensitySmall, DensityLarge, FileDownloadOutlined, QrCode2, RadioButtonChecked, RadioButtonUnchecked,
} from "@mui/icons-material";
// Components
import {DeleteAnyDialog} from "@/components/UI";
import {DownloadDialog} from "@/components/Download";
import ProductUrlDialog from "@/components/UI/Dialogs/ProductUrlDialog/ProductUrlDialog";
import {Badge} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";
// Data
import {useData} from "@/context/PackagingDataProvider";
// Misc
import {formatJSIso8601ToHumanString} from "@/utils/timeFunctions";
// @ts-ignore
import QRCodePlaceholder from "@/assets/png/qrcode-preview.png";
// Hooks
import {useModule} from "@/context/ModuleProvider";
import {useUser} from "@/context/UserProvider";

export default function ProductsDataTable(props: any) {

    const {
        gs1,
        loading,
        dataTableEmptyMessage,
        filters,
        dataTableFirst,
        onPageChange,
        products,
        selection, setSelection,
        multipleSelection, setMultipleSelection,
        seeInAnalytics,
        disableActions, columns,
        handleChangeDisplayedColumns,
        handleChangeFilters,
        t
    } = props

    const {
        deleteAny,
        getProducts,
        getSingleQRCodePreview,
        getGS1QRCodePreview,
        getSingleProductUrls,
        totalProducts,
        archiveProducts, restoreProducts,
        quickDownloadSingleQRCode,
        downloadProductsSpreadsheet
    } = useData()

    const {modules} = useModule()
    const {fullOrganisation, getFullOrganisation} = useUser()
    const navigate = useNavigate()

    // The products list endpoint returns `owner` (the attributed user's email,
    // or the "OWNER" role enum when the product belongs to the org owner) but
    // not the owner's email. Resolve attributed_user_uuid -> email from the org
    // tree so the Creator column shows the real email instead of a role label.
    useEffect(() => {
        if (!fullOrganisation) getFullOrganisation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const emailByUuid = useMemo(() => {
        const map: Record<string, string> = {}
        const walk = (org: any) => {
            if (!org) return
            ;(org.users ?? []).forEach((u: any) => {
                if (u?.user_id && u?.email) map[u.user_id] = u.email
            })
            ;(org.sub_organisations ?? []).forEach(walk)
        }
        walk(fullOrganisation)
        return map
    }, [fullOrganisation])

    // Resolve a product's creator email: prefer the org-tree lookup by uuid,
    // then fall back to `owner` when it already holds an email (not a role enum).
    const resolveCreatorEmail = (rowData: any): string => {
        const fromUuid = rowData?.attributed_user_uuid ? emailByUuid[rowData.attributed_user_uuid] : undefined
        if (fromUuid) return fromUuid
        if (typeof rowData?.owner === "string" && rowData.owner.includes("@")) return rowData.owner
        return ""
    }

    // Context menu anchor position
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null)
    // Columns menu anchor
    const [colMenuAnchor, setColMenuAnchor] = useState<null | HTMLElement>(null)
    // Previews
    const [qrcodePreviews, setQRCodePreviews] = useState<any[]>([])
    // Deletion
    const [deletion, setDeletion] = useState<any>(null)
    // Delete dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    // Download Dialog
    const [downloading, setDownloading] = useState<any>(null)
    // Bulk download dialog (current checkbox selection)
    const [bulkDownloadOpen, setBulkDownloadOpen] = useState<boolean>(false)
    // URLs
    const [openUrlsDialog, setOpenUrlsDialog] = useState<any>({open: false, product: null})
    // Short URLs (per-product, hydrated on demand — the products list endpoint
    // doesn't return them). "" means fetched-but-none, undefined means loading.
    const [shortUrls, setShortUrls] = useState<Record<string, string>>({})
    const shortUrlsRequested = useRef<Set<string>>(new Set())

    useEffect(() => {
        if (!!products && products.length > 0 && !!filters) {
            hydratePreviews().then(() => null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products])

    const hydratePreviews = async () => {
        for (let obj of products) {
            if (!obj.archived) {
                if (obj.type === "" || obj.type === "Standard") {
                    await getSingleQRCodePreview(obj.qr_code.id).then((res: any) => {
                        setQRCodePreviews((prev: any) => ({...prev, [obj.qr_code.id]: res === undefined ? "ERROR" : res}))
                    })
                } else if (obj.type === "GS1") {
                    await getGS1QRCodePreview(obj.qr_code.id).then((res: any) => {
                        setQRCodePreviews((prev: any) => ({...prev, [obj.qr_code.id]: res === undefined ? "ERROR" : res}))
                    })
                }
            }
        }
    }

    // Context menu
    const handleContextMenu = (event: React.MouseEvent, rowData: any) => {
        event.preventDefault()
        setSelection(rowData)
        setContextMenu({mouseX: event.clientX, mouseY: event.clientY})
    }

    const handleCloseContextMenu = () => {
        setContextMenu(null)
        setSelection(null)
    }

    const isMultiple = Boolean(multipleSelection && multipleSelection.length > 1)
    const isError = (row: any) => qrcodePreviews[row?.qr_code?.id] === "ERROR"

    // CRUD
    const archiveProduct = () => {
        archiveProducts([deletion.id]).then((archived: any) => {if (!!archived) getProducts()})
        setOpenDeleteDialog(false)
        setDeletion(null)
        setSelection(null)
        setMultipleSelection(null)
    }

    const restoreProduct = (uuid: any) => {
        restoreProducts([uuid]).then((restored: any) => {if (!!restored) getProducts()})
        setSelection(null)
        setMultipleSelection(null)
    }

    const deleteProduct = () => {
        deleteAny(deletion, "product").then((deleted: any) => {if (!!deleted) getProducts()})
        setOpenDeleteDialog(false)
        setDeletion(null)
        setSelection(null)
        setMultipleSelection(null)
    }

    // Handlers
    const handleEdit = (rowData: any) => {
        if (rowData.type === "GS1") navigate("/products/gs1/edit", {state: {...rowData}})
        else navigate("/products/edit", {state: {...rowData}})
    }

    const handleDelete = (rowData: any) => {
        setDeletion(rowData)
        setOpenDeleteDialog(true)
    }

    const handleOpenUrl = (rowData: any) => {
        setOpenUrlsDialog({open: true, product: rowData})
    }

    // Multi-select
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) setMultipleSelection(products)
        else setMultipleSelection([])
    }

    const handleToggleSelect = (rowData: any) => {
        const selected = multipleSelection ?? []
        const isSelected = selected.some((p: any) => p.id === rowData.id)
        if (isSelected) setMultipleSelection(selected.filter((p: any) => p.id !== rowData.id))
        else setMultipleSelection([...selected, rowData])
    }

    // Active optional columns (always-on: Product, Status, Actions)
    const showBrand = !!columns?.filterBrand
    const showRange = !!columns?.filterRange
    const showType = !!modules?.gs1 && !!columns?.filterType
    const showGtin = !!columns?.filterShortUrl && gs1
    // NB: `filterShortUrl` is the legacy GTIN column key — the short URL column
    // uses its own `filterProductShortUrl` key.
    const showShortUrl = !!columns?.filterProductShortUrl
    const showOwner = !!columns?.filterMember
    const showDate = !!columns?.filterDate

    const totalColumns = 3 + [showBrand, showRange, showType, showGtin, showShortUrl, showOwner, showDate].filter(Boolean).length

    // Hydrate short URLs for the visible rows, only while the column is shown
    // (one request per product — same pattern as the QR preview hydration).
    useEffect(() => {
        if (!showShortUrl || !products || products.length === 0) return
        const hydrate = async () => {
            for (const obj of products) {
                if (obj.archived || shortUrlsRequested.current.has(obj.id)) continue
                shortUrlsRequested.current.add(obj.id)
                await getSingleProductUrls(obj.id).then((res: any) => {
                    setShortUrls((prev) => ({...prev, [obj.id]: res?.product_short_url ?? ""}))
                })
            }
        }
        hydrate().then(() => null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, showShortUrl])

    // Column-toggle menu (Destination url is shown inline in the Product cell,
    // so it isn't offered here — see TODO in the PR description).
    const columnToggles = [
        {label: "created_at", key: "filterDate"},
        {label: "brand", key: "filterBrand"},
        {label: "range", key: "filterRange"},
        ...(gs1 ? [{label: "type", key: "filterType"}] : []),
        ...(gs1 ? [{label: "GTIN", key: "filterShortUrl"}] : []),
        {label: "short_url", key: "filterProductShortUrl"},
        {label: "owner", key: "filterMember"},
    ]

    const previewSize = filters?.lineHeight ?? 40

    // ── Status badge (derived: archived / preview-error / active) ──
    const renderStatus = (rowData: any) => {
        if (rowData.archived)
            return <Badge tone={"gray"} icon={<Inventory sx={{fontSize: 12}} />}>{t("archived")}</Badge>
        if (isError(rowData))
            return <Badge tone={"red"} dot>{t("error_preview")}</Badge>
        return <Badge tone={"green"} dot>{t("active")}</Badge>
    }

    const renderType = (rowData: any) => {
        if (rowData.type === "GS1") return <Badge tone={"blue"}>{t("gs1")}</Badge>
        return <Badge tone={"gray"}>{t("geolocation")}</Badge>
    }

    // ── Preview thumbnail (skeleton / archived / error / image) ──
    const renderPreview = (rowData: any) => {
        if (!rowData || rowData.qr_code.id === "" || !rowData.qr_code.id) return null

        const thumb = (inner: React.ReactNode) => (
            <Box
                sx={{
                    width: previewSize + 8,
                    height: previewSize + 8,
                    borderRadius: tk.radius.sm,
                    border: `1px solid ${tk.color.border}`,
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                }}
            >
                {inner}
            </Box>
        )

        if (rowData.archived) return (
            <Tooltip title={t("archived_tooltip")} placement={"top"} arrow>
                {thumb(<QrCode2 sx={{fontSize: previewSize * 0.6, color: tk.color.textTertiary, opacity: 0.5}} />)}
            </Tooltip>
        )

        if (!qrcodePreviews[rowData.qr_code.id]) return (
            <Skeleton variant={"rounded"} height={previewSize + 8} width={previewSize + 8} sx={{borderRadius: tk.radius.sm}} />
        )

        if (qrcodePreviews[rowData.qr_code.id] === "ERROR") return (
            <Tooltip title={t("error_preview_sub")} placement={"top"} arrow>
                {thumb(<QrCode2 sx={{fontSize: previewSize * 0.6, color: tk.color.red, opacity: 0.6}} />)}
            </Tooltip>
        )

        return thumb(
            <img height={previewSize} width={previewSize} style={{transition: ".25s ease-in-out"}}
                 src={qrcodePreviews[rowData.qr_code.id]} alt={"preview"} />
        )
    }

    // ── Row actions ──
    const actionBtnSx = {
        width: 34,
        height: 34,
        borderRadius: tk.radius.sm,
        // textSecondary, not textTertiary: the light grey read as "disabled".
        // MUI's real disabled state still greys them out via Mui-disabled.
        color: tk.color.textSecondary,
        "&:hover": {backgroundColor: tk.color.bg, color: tk.color.textPrimary},
    }

    const renderActions = (rowData: any) => {
        const _productError = isError(rowData)

        if (rowData.archived) return (
            <Stack direction={"row"} spacing={0.5} justifyContent={"flex-end"}>
                <Tooltip placement={"top"} arrow title={`${t("restore")}`}>
                    <span>
                        <IconButton size={"small"} sx={{...actionBtnSx, color: tk.color.greenDark}}
                                    onClick={() => restoreProduct(rowData.id)} disabled={disableActions || _productError}>
                            <Unarchive sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip placement={"top"} arrow title={`${t("delete")}`}>
                    <span>
                        <IconButton size={"small"} sx={{...actionBtnSx, "&:hover": {backgroundColor: tk.color.red100, color: tk.color.red}}}
                                    onClick={() => handleDelete(rowData)} disabled={disableActions || _productError}>
                            <Delete sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        )

        return (
            <Stack direction={"row"} spacing={0.5} justifyContent={"flex-end"}>
                <Tooltip placement={"top"} arrow title={`${t("edit")}`}>
                    <span>
                        <IconButton size={"small"} sx={actionBtnSx} onClick={() => handleEdit(rowData)} disabled={disableActions || _productError}>
                            <Edit sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip placement={"top"} arrow title={`${t("redirection_urls")}`}>
                    <span>
                        <IconButton size={"small"} sx={actionBtnSx} onClick={() => handleOpenUrl(rowData)} disabled={disableActions || _productError}>
                            <LinkIcon sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip placement={"top"} arrow title={`${t("download")}`}>
                    <span>
                        <IconButton size={"small"} sx={actionBtnSx} onClick={() => setDownloading(rowData)} disabled={_productError} data-step-target={"download-product"}>
                            <Download sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip placement={"top"} arrow title={`${t("archive")}`}>
                    <span>
                        <IconButton size={"small"} sx={actionBtnSx} onClick={() => handleDelete(rowData)} disabled={disableActions || _productError}>
                            <Inventory sx={{fontSize: 20}} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        )
    }

    const rowsPerPage = filters?.maxRows ?? 10
    const page = Math.max(0, Math.floor(dataTableFirst / rowsPerPage))
    const selectedCount = multipleSelection?.length ?? 0

    const thSx = {
        py: 1.25,
        px: 2.5,
        fontSize: "11.5px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: tk.color.textTertiary,
        backgroundColor: tk.color.bg,
        borderBottom: `1px solid ${tk.color.border}`,
        whiteSpace: "nowrap",
    }
    const tdSx = {
        py: 1.75,
        px: 2.5,
        fontSize: "13.5px",
        color: tk.color.textPrimary,
        borderBottom: `1px solid ${tk.color.borderLight}`,
    }

    return (
        <>
            {/* Context Menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu !== null ? {top: contextMenu.mouseY, left: contextMenu.mouseX} : undefined}
            >
                <MenuItem disabled>
                    <Typography variant="body2" fontWeight="bold">{selection?.name}</Typography>
                </MenuItem>
                <Divider/>
                <MenuItem
                    onClick={() => { handleEdit(selection); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || selection?.archived || disableActions || isError(selection))}
                >
                    <ListItemIcon><Edit fontSize="small"/></ListItemIcon>
                    <ListItemText>{t("common:edit")}</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => { handleOpenUrl(selection); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || selection?.archived || disableActions || isError(selection))}
                >
                    <ListItemIcon><LinkIcon fontSize="small"/></ListItemIcon>
                    <ListItemText>{t("redirection_urls")}</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => { seeInAnalytics(selection); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || selection?.archived || isError(selection))}
                >
                    <ListItemIcon><QueryStats fontSize="small"/></ListItemIcon>
                    <ListItemText>{t("product_see_in_analytics")}</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem
                    onClick={() => { quickDownloadSingleQRCode(selection.qr_code.id, selection.name, selection.gtin); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || selection?.archived || isError(selection))}
                >
                    <ListItemIcon><Downloading fontSize="small"/></ListItemIcon>
                    <ListItemText>{t("quick_download")}</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => { setDownloading(selection); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || selection?.archived || isError(selection))}
                >
                    <ListItemIcon><Download fontSize="small"/></ListItemIcon>
                    <ListItemText>{t("export_as")}</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem
                    onClick={() => { handleDelete(selection); handleCloseContextMenu() }}
                    disabled={Boolean(isMultiple || disableActions || isError(selection))}
                >
                    <ListItemIcon>
                        {selection?.archived ? <Delete fontSize="small"/> : <Inventory fontSize="small"/>}
                    </ListItemIcon>
                    <ListItemText>{selection?.archived ? t("common:delete") : t("archive")}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Columns menu */}
            <Menu
                open={Boolean(colMenuAnchor)}
                onClose={() => setColMenuAnchor(null)}
                anchorEl={colMenuAnchor}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                MenuListProps={{dense: true}}
                disableScrollLock
            >
                {columnToggles.map((col: any, i: number) => {
                    const active = !!columns?.[col.key]
                    return (
                        <MenuItem onClick={() => handleChangeDisplayedColumns(col.key)} key={i}>
                            <ListItemIcon>
                                {active
                                    ? <RadioButtonChecked color={"primary"} fontSize={"small"} />
                                    : <RadioButtonUnchecked fontSize={"small"} />}
                            </ListItemIcon>
                            <ListItemText>{t(col.label)}</ListItemText>
                        </MenuItem>
                    )
                })}
            </Menu>

            {/* ─── Table card ─── */}
            <Box
                sx={{
                    backgroundColor: tk.color.surface,
                    border: `1px solid ${tk.color.border}`,
                    borderRadius: tk.radius.card,
                    overflow: "hidden",
                }}
            >
                {/* Header bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        px: 2.5,
                        py: 1.5,
                        borderBottom: `1px solid ${tk.color.borderLight}`,
                    }}
                >
                    <Stack direction={"row"} spacing={1} alignItems={"center"}>
                        <Checkbox
                            size={"small"}
                            sx={{p: 0.5}}
                            indeterminate={selectedCount > 0 && selectedCount < (products?.length ?? 0)}
                            checked={!!products && products.length > 0 && selectedCount === products.length}
                            onChange={handleSelectAll}
                        />
                        <Typography sx={{fontSize: "13px", fontWeight: 600}}>
                            {totalProducts ?? 0} {t("products").toLowerCase()}
                            {selectedCount > 0 && (
                                <Box component={"span"} sx={{color: tk.color.textTertiary, fontWeight: 400}}>
                                    {" · "}{t("n_selected", {n: selectedCount, defaultValue: `${selectedCount} selected`})}
                                </Box>
                            )}
                        </Typography>
                    </Stack>

                    <Stack direction={"row"} spacing={0.5} alignItems={"center"} data-step-target={"see-analytics"}>
                        {selectedCount > 0 && (
                            <Button
                                size={"small"}
                                startIcon={<Download sx={{fontSize: 15}} />}
                                onClick={() => setBulkDownloadOpen(true)}
                                sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, color: tk.color.textSecondary}}
                            >
                                {t("download")}
                            </Button>
                        )}
                        {selectedCount > 0 && (
                            <Button
                                size={"small"}
                                startIcon={<QueryStats sx={{fontSize: 15}} />}
                                onClick={() => seeInAnalytics()}
                                sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 600, color: tk.color.textSecondary}}
                            >
                                {t("product_see_in_analytics")}
                            </Button>
                        )}
                        <Button
                            size={"small"}
                            startIcon={<ViewColumnOutlined sx={{fontSize: 15}} />}
                            onClick={(e) => setColMenuAnchor(e.currentTarget)}
                            sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 500, color: tk.color.textSecondary, "&:hover": {backgroundColor: tk.color.bg}}}
                        >
                            {t("columns")}
                        </Button>
                        <Tooltip title={previewSize === 80 ? t("compact_rows", "Compact rows") : t("comfortable_rows", "Comfortable rows")} arrow placement={"top"}>
                            <IconButton
                                size={"small"}
                                onClick={() => handleChangeFilters("lineHeight", previewSize === 80 ? 40 : 80)}
                                sx={{color: tk.color.textSecondary, "&:hover": {backgroundColor: tk.color.bg}}}
                            >
                                {previewSize === 80 ? <DensitySmall sx={{fontSize: 16}} /> : <DensityLarge sx={{fontSize: 16}} />}
                            </IconButton>
                        </Tooltip>
                        {/* Spreadsheet export of the current selection
                            (POST /packaging/export/product). */}
                        <Tooltip title={selectedCount ? "" : t("common:export_select_products")} arrow placement={"top"}>
                            <span>
                                <Button
                                    size={"small"}
                                    disabled={!selectedCount}
                                    onClick={() => downloadProductsSpreadsheet((multipleSelection ?? []).map((p: any) => p.id))}
                                    startIcon={<FileDownloadOutlined sx={{fontSize: 15}} />}
                                    sx={{textTransform: "none", fontSize: "12.5px", fontWeight: 500, color: tk.color.textSecondary, "&:hover": {backgroundColor: tk.color.bg}}}
                                >
                                    {t("export")}
                                    {selectedCount ? ` (${selectedCount})` : ""}
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding={"checkbox"} sx={{...thSx, pl: 2.5}} />
                                <TableCell sx={thSx}>{t("product", {defaultValue: "Product"})}</TableCell>
                                {showBrand && <TableCell sx={thSx}>{t("brand")}</TableCell>}
                                {showRange && <TableCell sx={thSx}>{t("range")}</TableCell>}
                                {showType && <TableCell sx={thSx}>{t("type")}</TableCell>}
                                {showGtin && <TableCell sx={thSx}>GTIN</TableCell>}
                                {showShortUrl && <TableCell sx={thSx}>{t("short_url")}</TableCell>}
                                {showOwner && <TableCell sx={thSx}>{t("owner")}</TableCell>}
                                <TableCell sx={thSx}>{t("status")}</TableCell>
                                {showDate && <TableCell sx={thSx}>{t("created_at")}</TableCell>}
                                <TableCell align={"right"} sx={{...thSx, minWidth: 150}}>{t("actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({length: Math.min(rowsPerPage, 5)}).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={totalColumns} sx={{px: 2.5, py: 1.5, borderBottom: `1px solid ${tk.color.borderLight}`}}>
                                            <Skeleton variant="rounded" height={42} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (!products || products.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={totalColumns} align="center" sx={{py: 6, color: tk.color.textSecondary}}>
                                        {t(dataTableEmptyMessage)}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((rowData: any) => {
                                    const isRowSelected = multipleSelection?.some((p: any) => p.id === rowData.id) ?? false
                                    return (
                                        <TableRow
                                            key={rowData.id}
                                            selected={isRowSelected}
                                            onContextMenu={(e) => handleContextMenu(e, rowData)}
                                            sx={{
                                                cursor: "context-menu",
                                                transition: "background .12s",
                                                "&:last-child td": {borderBottom: "none"},
                                                "&:hover": {backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)},
                                                "&.Mui-selected": {backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)},
                                                "&.Mui-selected:hover": {backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)},
                                                opacity: rowData.archived ? 0.7 : 1,
                                            }}
                                        >
                                            <TableCell padding={"checkbox"} sx={{...tdSx, pl: 2.5}}>
                                                <Checkbox size={"small"} sx={{p: 0.5}} checked={isRowSelected} onChange={() => handleToggleSelect(rowData)} />
                                            </TableCell>
                                            <TableCell sx={tdSx}>
                                                <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                                                    {renderPreview(rowData)}
                                                    <Box sx={{minWidth: 0}}>
                                                        <Typography sx={{fontSize: "13.5px", fontWeight: 600, color: tk.color.textPrimary}}>
                                                            {rowData.name}
                                                        </Typography>
                                                        {rowData.default_url && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "12px",
                                                                    color: tk.color.textTertiary,
                                                                    fontFamily: tk.font.mono,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    maxWidth: 280,
                                                                }}
                                                            >
                                                                {rowData.default_url}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            {showBrand && (
                                                <TableCell sx={tdSx}>
                                                    {rowData.range?.brand?.name
                                                        ? <Badge tone={"gray"}>{rowData.range.brand.name}</Badge>
                                                        : "—"}
                                                </TableCell>
                                            )}
                                            {showRange && (
                                                <TableCell sx={{...tdSx, color: tk.color.textSecondary}}>
                                                    {rowData.range?.name ?? "—"}
                                                </TableCell>
                                            )}
                                            {showType && (
                                                <TableCell sx={tdSx}>{renderType(rowData)}</TableCell>
                                            )}
                                            {showGtin && (
                                                <TableCell sx={{...tdSx, fontFamily: tk.font.mono, fontSize: "12.5px", color: tk.color.textSecondary}}>
                                                    {rowData.gtin || "—"}
                                                </TableCell>
                                            )}
                                            {showShortUrl && (() => {
                                                const shortUrl = rowData.archived ? "" : shortUrls[rowData.id]
                                                return (
                                                    <TableCell sx={{...tdSx, fontFamily: tk.font.mono, fontSize: "12.5px", color: tk.color.textSecondary}}>
                                                        {rowData.archived ? "—"
                                                            : shortUrl === undefined ? (
                                                                <Skeleton variant={"text"} width={130} />
                                                            ) : shortUrl === "" ? "—" : (
                                                                <Tooltip title={shortUrl} placement={"top"} arrow>
                                                                    <Box
                                                                        component={"span"}
                                                                        sx={{
                                                                            display: "inline-block",
                                                                            maxWidth: 180,
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            verticalAlign: "middle",
                                                                        }}
                                                                    >
                                                                        {shortUrl}
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                    </TableCell>
                                                )
                                            })()}
                                            {showOwner && (() => {
                                                const creatorEmail = resolveCreatorEmail(rowData)
                                                return (
                                                    <TableCell sx={tdSx}>
                                                        {(rowData.attributed_org_name || creatorEmail) ? (
                                                            <>
                                                                {rowData.attributed_org_name && (
                                                                    <Typography variant="body2" fontWeight={600}>{rowData.attributed_org_name}</Typography>
                                                                )}
                                                                {creatorEmail && (
                                                                    <Typography variant="caption" color="text.secondary">{creatorEmail}</Typography>
                                                                )}
                                                            </>
                                                        ) : "—"}
                                                    </TableCell>
                                                )
                                            })()}
                                            <TableCell sx={tdSx}>{renderStatus(rowData)}</TableCell>
                                            {showDate && (
                                                <TableCell sx={{...tdSx, color: tk.color.textTertiary, fontSize: "13px"}}>
                                                    {formatJSIso8601ToHumanString(rowData.created_at)}
                                                </TableCell>
                                            )}
                                            <TableCell align={"right"} sx={tdSx}>
                                                {renderActions(rowData)}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer pagination */}
                <Box sx={{borderTop: `1px solid ${tk.color.borderLight}`}}>
                    <TablePagination
                        component="div"
                        count={totalProducts}
                        page={page}
                        onPageChange={(_, newPage) => onPageChange({page: newPage, first: newPage * rowsPerPage, rows: rowsPerPage})}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => onPageChange({rows: parseInt(e.target.value), page: 0, first: 0})}
                        rowsPerPageOptions={[10, 20, 50, 100, 150, 200]}
                        labelRowsPerPage={t("common:rows_per_page")}
                        labelDisplayedRows={({from, to, count}) => `${from}–${to} ${t("common:of")} ${count}`}
                        sx={{
                            ".MuiTablePagination-toolbar": {minHeight: 52},
                            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                fontSize: "12.5px",
                                color: tk.color.textTertiary,
                            },
                        }}
                    />
                </Box>
            </Box>

            <DeleteAnyDialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                onDelete={() => deleteProduct()}
                onArchive={() => archiveProduct()}
                assetName={deletion?.name}
                archived={deletion?.archived}
                text={t("delete_product_alert")}
                {...{t}}
            />

            <ProductUrlDialog
                open={openUrlsDialog.open}
                onClose={() => setOpenUrlsDialog({open: false, product: null})}
                product={openUrlsDialog.product}
                {...{t}}
            />

            <DownloadDialog
                open={!!downloading}
                onClose={() => setDownloading(null)}
                product={downloading}
                {...{t}}
            />

            <DownloadDialog
                open={bulkDownloadOpen}
                onClose={() => setBulkDownloadOpen(false)}
                products={multipleSelection ?? []}
                {...{t}}
            />
        </>
    )
}
